/**
 * Buy 3 Get 3 Free Promotion Handler
 * Manages the interactive elements of the promotion system
 */

class Buy3Get3Promotion {
  constructor() {
    // Get settings from Liquid template via global variables
    const settings = window.promotionSettings || {};
    
    this.promotionCollections = Array.isArray(settings.collections) 
      ? settings.collections.map(handle => handle.trim()) 
      : ['thc-products', 'cbd-products'];
    this.minQuantity = settings.minQuantity || 3;
    this.freeQuantity = settings.freeQuantity || 3;
    this.enabled = settings.enabled !== false; // Default to true
    this.title = settings.title || 'Buy 3 Get 3 FREE!';
    this.description = settings.description || 'Add 3 or more items from eligible collections and get 3 items absolutely free!';
    
    console.log('Buy3Get3Promotion initialized with settings:', {
      enabled: this.enabled,
      collections: this.promotionCollections,
      minQuantity: this.minQuantity,
      freeQuantity: this.freeQuantity
    });
    
    // Only initialize if promotion is enabled
    if (this.enabled) {
      this.init();
    }
  }

  init() {
    // Initialize promotion tracking
    this.bindEvents();
    this.updatePromotionStatus();
  }

  bindEvents() {
    // Handle all click events with delegation
    document.addEventListener('click', (e) => {
      // Handle "Select Your Free Items" button clicks
      if (e.target.matches('[data-action="select-free-items"]')) {
        e.preventDefault();
        this.openFreeItemSelector();
        return;
      }
      
      // Skip collections toggle as it's handled by inline onclick
      // This prevents double handling of the same event
      
      // Close dropdown when clicking outside
      if (!e.target.closest('.promotion-banner__collections')) {
        this.closeAllCollectionDropdowns();
      }
    });

    // Listen for cart updates
    document.addEventListener('cart:refresh', () => {
      this.updatePromotionStatus();
    });

    // Listen for product additions to cart
    document.addEventListener('product:added-to-cart', (e) => {
      const { product } = e.detail;
      this.handleProductAdded(product);
    });
  }

  /**
   * Check if a product is eligible for the promotion
   */
  isEligibleProduct(product) {
    if (!product || !product.collections) return false;
    
    return this.promotionCollections.some(handle => 
      product.collections.some(collection => collection.handle === handle)
    );
  }

  /**
   * Calculate current promotion status from cart
   */
  calculatePromotionStatus(cartItems = []) {
    let eligibleItems = 0;
    let freeItems = 0;

    cartItems.forEach(item => {
      if (this.isEligibleProduct(item.product)) {
        if (item.original_price > 0) {
          eligibleItems += item.quantity;
        } else {
          freeItems += item.quantity;
        }
      }
    });

    const eligibleSets = Math.floor(eligibleItems / this.minQuantity);
    const maxFreeItems = eligibleSets * this.freeQuantity;
    const freeItemsNeeded = Math.max(0, maxFreeItems - freeItems);

    return {
      eligibleItems,
      freeItems,
      eligibleSets,
      maxFreeItems,
      freeItemsNeeded,
      promotionActive: eligibleItems >= this.minQuantity,
      progressPercentage: Math.min(100, (eligibleItems / this.minQuantity) * 100)
    };
  }

  /**
   * Update promotion status displays
   */
  updatePromotionStatus() {
    // This would typically fetch current cart state
    // For now, we'll rely on the Liquid templating for display
    console.log('Promotion status updated');
  }

  /**
   * Toggle collections dropdown
   */
  toggleCollectionsDropdown(button) {
    console.log('Toggle collections clicked', button);
    
    const collectionsContainer = button.closest('.promotion-banner__collections');
    const dropdown = collectionsContainer.querySelector('[data-collections-dropdown]');
    
    if (!dropdown) {
      console.error('Dropdown not found');
      return;
    }
    
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    console.log('Current state:', isExpanded ? 'expanded' : 'collapsed');
    
    // Close all other dropdowns first
    this.closeAllCollectionDropdowns();
    
    if (!isExpanded) {
      console.log('Opening dropdown');
      button.setAttribute('aria-expanded', 'true');
      dropdown.hidden = false;
      
      // Force reflow and then show
      dropdown.offsetHeight;
      dropdown.style.display = 'block';
    } else {
      console.log('Closing dropdown');
      button.setAttribute('aria-expanded', 'false');
      dropdown.hidden = true;
      dropdown.style.display = 'none';
    }
  }

  /**
   * Close all collection dropdowns
   */
  closeAllCollectionDropdowns() {
    const dropdowns = document.querySelectorAll('[data-collections-dropdown]');
    const toggles = document.querySelectorAll('[data-action="toggle-collections"]');
    
    dropdowns.forEach(dropdown => {
      dropdown.hidden = true;
      dropdown.style.display = 'none';
    });
    
    toggles.forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Handle when a product is added to cart
   */
  handleProductAdded(product) {
    if (this.isEligibleProduct(product)) {
      // Show notification about promotion progress
      this.showPromotionNotification(product);
    }
  }

  /**
   * Show promotion notification
   */
  showPromotionNotification(product) {
    // Create a temporary notification
    const notification = document.createElement('div');
    notification.className = 'promotion-notification';
    notification.innerHTML = `
      <div class="promotion-notification__content">
        <span class="promotion-notification__icon">🎉</span>
        <div class="promotion-notification__text">
          <strong>Great choice!</strong> This item counts toward your Buy ${this.minQuantity} Get ${this.freeQuantity} FREE promotion.
        </div>
      </div>
    `;

    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 5000);
  }

  /**
   * Open free item selector interface
   */
  openFreeItemSelector() {
    // Create modal/drawer for selecting free items
    const modal = this.createFreeItemModal();
    document.body.appendChild(modal);
    
    // Show modal
    setTimeout(() => {
      modal.classList.add('promotion-modal--visible');
    }, 10);
  }

  /**
   * Create free item selector modal
   */
  createFreeItemModal() {
    const modal = document.createElement('div');
    modal.className = 'promotion-modal';
    modal.innerHTML = `
      <div class="promotion-modal__overlay"></div>
      <div class="promotion-modal__content">
        <header class="promotion-modal__header">
          <h3 class="promotion-modal__title">Select Your Free Items</h3>
          <button type="button" class="promotion-modal__close" data-action="close-modal">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M12.5 3.5L3.5 12.5M3.5 3.5L12.5 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </header>
        <div class="promotion-modal__body">
          <p class="promotion-modal__description">
            Choose your free items from eligible collections. You can select up to <strong id="free-items-available">${this.freeQuantity}</strong> items.
          </p>
          <div class="promotion-modal__collections" id="free-item-collections">
            Loading eligible items...
          </div>
        </div>
        <footer class="promotion-modal__footer">
          <button type="button" class="button button--secondary" data-action="close-modal">
            Cancel
          </button>
          <button type="button" class="button button--primary" data-action="add-free-items" disabled>
            Add Selected Items
          </button>
        </footer>
      </div>
    `;

    // Bind modal events
    modal.addEventListener('click', (e) => {
      if (e.target.matches('[data-action="close-modal"]') || e.target.classList.contains('promotion-modal__overlay')) {
        this.closeFreeItemModal(modal);
      } else if (e.target.matches('[data-action="add-free-items"]')) {
        this.addSelectedFreeItems(modal);
      }
    });

    // Load eligible items
    this.loadEligibleFreeItems(modal);

    return modal;
  }

  /**
   * Load eligible items for free selection
   */
  async loadEligibleFreeItems(modal) {
    try {
      // This would typically make an AJAX request to get eligible products
      // For demonstration, we'll create a placeholder
      const collectionsContainer = modal.querySelector('#free-item-collections');
      
      const collectionLinks = this.promotionCollections
        .slice(0, 5) // Show only first 5 collections to keep it manageable
        .map(handle => `<a href="/collections/${handle}" target="_blank">${handle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</a>`)
        .join(', ');
      
      collectionsContainer.innerHTML = `
        <div class="promotion-modal__message">
          <p>To select free items:</p>
          <ol>
            <li>Browse eligible collections: ${collectionLinks}${this.promotionCollections.length > 5 ? ` and ${this.promotionCollections.length - 5} more...` : ''}</li>
            <li>Add items with $0 price to your cart</li>
            <li>Use discount code: <strong>BUY${this.minQuantity}GET${this.freeQuantity}FREE</strong> at checkout</li>
          </ol>
          <p><small>Note: This is a simplified implementation. In a full system, you would see product selection interface here.</small></p>
        </div>
      `;
    } catch (error) {
      console.error('Failed to load eligible items:', error);
    }
  }

  /**
   * Add selected free items to cart
   */
  addSelectedFreeItems(modal) {
    // This would add the selected items to cart
    // For now, just close the modal
    this.closeFreeItemModal(modal);
  }

  /**
   * Close free item modal
   */
  closeFreeItemModal(modal) {
    modal.classList.remove('promotion-modal--visible');
    setTimeout(() => {
      if (modal.parentNode) {
        modal.remove();
      }
    }, 300);
  }
}

// Initialize promotion system when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Wait for settings to be available
  const initializePromotion = () => {
    if (window.promotionSettings) {
      new Buy3Get3Promotion();
    } else {
      // Retry after a short delay if settings aren't loaded yet
      setTimeout(initializePromotion, 100);
    }
  };
  
  initializePromotion();
});

// Add CSS for modal and notifications
const promotionCSS = `
  .promotion-notification {
    position: fixed;
    top: 20px;
    right: 20px;
    background: #4CAF50;
    color: white;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10000;
    max-width: 350px;
    animation: slideInRight 0.3s ease;
  }

  .promotion-notification__content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .promotion-notification__icon {
    font-size: 20px;
    flex-shrink: 0;
  }

  .promotion-notification__text {
    font-size: 14px;
    line-height: 1.4;
  }

  .promotion-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10001;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }

  .promotion-modal--visible {
    opacity: 1;
    visibility: visible;
  }

  .promotion-modal__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  .promotion-modal__content {
    background: white;
    border-radius: 12px;
    max-width: 600px;
    width: 90vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    position: relative;
    transform: scale(0.95);
    transition: transform 0.3s ease;
  }

  .promotion-modal--visible .promotion-modal__content {
    transform: scale(1);
  }

  .promotion-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 24px 0;
    border-bottom: 1px solid #eee;
    margin-bottom: 24px;
  }

  .promotion-modal__title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }

  .promotion-modal__close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    color: #666;
    transition: all 0.2s ease;
  }

  .promotion-modal__close:hover {
    background: #f5f5f5;
    color: #333;
  }

  .promotion-modal__body {
    padding: 0 24px;
    flex: 1;
    overflow-y: auto;
  }

  .promotion-modal__description {
    margin: 0 0 20px;
    color: #666;
    line-height: 1.5;
  }

  .promotion-modal__message {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border-left: 4px solid #ff6b6b;
  }

  .promotion-modal__message ol {
    margin: 12px 0;
    padding-left: 20px;
  }

  .promotion-modal__message li {
    margin: 8px 0;
  }

  .promotion-modal__footer {
    padding: 24px;
    border-top: 1px solid #eee;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @media (max-width: 640px) {
    .promotion-notification {
      right: 10px;
      left: 10px;
      max-width: none;
    }

    .promotion-modal__content {
      width: 95vw;
      margin: 20px;
    }

    .promotion-modal__header,
    .promotion-modal__body,
    .promotion-modal__footer {
      padding-left: 16px;
      padding-right: 16px;
    }

    .promotion-modal__footer {
      flex-direction: column;
    }
  }
`;

// Inject CSS
const style = document.createElement('style');
style.textContent = promotionCSS;
document.head.appendChild(style);
